import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/layout/Layout';
import '../styles/curriculum-manager.css';

// ── Status chip ───────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  draft:     { bg: '#F3F4F6', border: '#D1D5DB', color: '#6B7280' },
  published: { bg: '#D1FAE5', border: '#6EE7B7', color: '#065F46' },
  archived:  { bg: '#F3F4F6', border: '#D1D5DB', color: '#9CA3AF' },
};

function StatusChip({ status }) {
  const s   = STATUS_STYLE[status] || STATUS_STYLE.draft;
  const MAP = { draft: 'Draft', published: 'Published', archived: 'Archived' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: s.bg, border: `1.5px solid ${s.border}`, color: s.color,
      whiteSpace: 'nowrap',
    }}>
      {MAP[status] || status}
    </span>
  );
}

const DIFF_COLOR = { Beginner: '#10B981', Intermediate: '#F59E0B', Advanced: '#EF4444' };

// ══════════════════════════════════════════════════════════════════════════════
// Activity Manager — manually curated sequencing activities. No AI, no import.
// ══════════════════════════════════════════════════════════════════════════════

const DIFFICULTIES = ['All', 'Beginner', 'Intermediate', 'Advanced'];
const STATUSES     = ['All', 'draft', 'published', 'archived'];

export const CurriculumManagerPage = () => {
  const { userData, currentUser } = useAuth();
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('All');
  const [diffFilter, setDiffFilter] = useState('All');
  const [statFilter, setStatFilter] = useState('All');
  const [working,    setWorking]    = useState({});
  const [actionErr,  setActionErr]  = useState(null);

  const isAdmin = userData?.role === 'admin' || currentUser?.email === 'mackayla.omalley00@gmail.com';

  // ── Load activities ─────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'generatedSequencingDecks'), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, snap => {
      setActivities(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
  }, []);

  const categories = ['All', ...new Set(activities.map(a => a.category).filter(Boolean))];

  // ── Filters ────────────────────────────────────────────────────────────────
  const filtered = activities.filter(a => {
    if (catFilter  !== 'All' && a.category   !== catFilter)  return false;
    if (diffFilter !== 'All' && a.difficulty !== diffFilter) return false;
    if (statFilter !== 'All' && a.status     !== statFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.title || '').toLowerCase().includes(q)
          || (a.category || '').toLowerCase().includes(q)
          || (a.subcategory || '').toLowerCase().includes(q)
          || (a.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  // ── Direct status update (no Cloud Function — gated by isAdmin() rule) ──────
  const handleStatusChange = async (activityId, newStatus) => {
    setWorking(w => ({ ...w, [activityId]: true }));
    setActionErr(null);
    try {
      await updateDoc(doc(db, 'generatedSequencingDecks', activityId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        ...(newStatus === 'published' ? { publishedAt: serverTimestamp() } : {}),
      });
    } catch (err) {
      setActionErr(err.message || 'Failed to update status.');
    } finally {
      setWorking(w => ({ ...w, [activityId]: false }));
    }
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalSteps = activities.reduce((s, a) => s + (a.stepCount || 0), 0);
  const byStatus   = activities.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1; return acc;
  }, {});

  if (!isAdmin) {
    return (
      <Layout>
        <div className="cm-page" style={{ textAlign: 'center', paddingTop: 80 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ margin: '0 0 8px', color: '#111827' }}>Admin Access Required</h2>
          <p style={{ color: '#6B7280', marginBottom: 24 }}>
            Your account needs the <code>admin</code> role in Firestore to access the Activity Manager.
          </p>
          <button className="cm-btn cm-btn--primary" onClick={() => navigate('/sequencing')}>
            Back to Library
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="cm-page">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="cm-header">
          <div>
            <h1 className="cm-title">Activity Manager</h1>
            <p className="cm-subtitle">
              {activities.length} activities · {totalSteps} steps total
            </p>
          </div>
          <button className="cm-btn cm-btn--primary" onClick={() => navigate('/sequencing/generate')}>
            + New Activity
          </button>
        </div>

        {/* ── Stats bar ──────────────────────────────────────────────────── */}
        <div className="cm-stats-bar">
          {[
            { label: 'Draft',     value: byStatus.draft || 0,     color: '#6B7280' },
            { label: 'Published', value: byStatus.published || 0, color: '#10B981' },
            { label: 'Archived',  value: byStatus.archived || 0,  color: '#D1D5DB' },
          ].map(s => (
            <div key={s.label} className="cm-stat">
              <div className="cm-stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="cm-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Filters ────────────────────────────────────────────────────── */}
        <div className="cm-filters">
          <div className="cm-search-wrap">
            <span className="cm-search-icon">🔍</span>
            <input
              className="cm-search"
              placeholder="Search activities, categories, tags…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <select className="cm-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            {categories.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>

          <select className="cm-select" value={diffFilter} onChange={e => setDiffFilter(e.target.value)}>
            {DIFFICULTIES.map(d => <option key={d} value={d}>{d === 'All' ? 'All Difficulties' : d}</option>)}
          </select>

          <select className="cm-select" value={statFilter} onChange={e => setStatFilter(e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>)}
          </select>
        </div>

        {actionErr && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10,
            padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#DC2626' }}>
            {actionErr}
          </div>
        )}

        {/* ── Results count ──────────────────────────────────────────────── */}
        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12, fontWeight: 600 }}>
          {loading ? 'Loading…' : `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#9CA3AF' }}>
            Loading activities…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#9CA3AF' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 14 }}>
              {activities.length === 0
                ? 'No activities yet. Click "+ New Activity" to create your first one.'
                : 'No activities match your filters.'}
            </div>
          </div>
        ) : (
          <div className="cm-table-wrap">
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Category</th>
                  <th>Subcategory</th>
                  <th>Difficulty</th>
                  <th>Steps</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const isBusy = !!working[a.id];
                  return (
                    <tr key={a.id} className="cm-table-row">
                      <td>
                        <div style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>{a.title || 'Untitled'}</div>
                        {a.tags?.length > 0 && (
                          <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                            {a.tags.slice(0, 4).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: '#374151' }}>{a.category}</td>
                      <td style={{ fontSize: 13, color: '#6B7280' }}>{a.subcategory}</td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 12,
                          color: DIFF_COLOR[a.difficulty] || '#6B7280',
                          background: (DIFF_COLOR[a.difficulty] || '#6B7280') + '18',
                          border: `1.5px solid ${(DIFF_COLOR[a.difficulty] || '#6B7280')}33`,
                        }}>
                          {a.difficulty}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, fontWeight: 700, color: '#374151', textAlign: 'center' }}>
                        {a.stepCount || 0}
                      </td>
                      <td><StatusChip status={a.status} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            className="cm-btn cm-btn--primary"
                            style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => navigate(`/sequencing/review/${a.id}`)}
                            disabled={isBusy}
                          >
                            ✏ Edit
                          </button>

                          {a.status === 'draft' && (
                            <button
                              className="cm-btn"
                              style={{ fontSize: 11, padding: '4px 10px' }}
                              onClick={() => handleStatusChange(a.id, 'published')}
                              disabled={isBusy}
                            >
                              {isBusy ? '…' : 'Publish'}
                            </button>
                          )}
                          {a.status === 'published' && (
                            <button
                              className="cm-btn"
                              style={{ fontSize: 11, padding: '4px 10px' }}
                              onClick={() => handleStatusChange(a.id, 'draft')}
                              disabled={isBusy}
                            >
                              {isBusy ? '…' : 'Unpublish'}
                            </button>
                          )}
                          {a.status !== 'archived' && (
                            <button
                              className="cm-btn cm-btn--danger"
                              style={{ fontSize: 11, padding: '4px 10px' }}
                              onClick={() => handleStatusChange(a.id, 'archived')}
                              disabled={isBusy}
                            >
                              Archive
                            </button>
                          )}
                          {a.status === 'archived' && (
                            <button
                              className="cm-btn"
                              style={{ fontSize: 11, padding: '4px 10px' }}
                              onClick={() => handleStatusChange(a.id, 'draft')}
                              disabled={isBusy}
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </Layout>
  );
};
